/**
 * Upgrade Page Constants
 * Pricing tiers, features, and hero stats
 */

import type { PricingTier, HeroStat, FAQItem } from '../types';

// ============================================================================
// Pricing Tiers
// ============================================================================

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Get started with basic tracking',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: [
      'Track up to 5 products',
      'Stock alerts',
      'Basic price history',
      'Browser extension',
      'Mobile app access',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Perfect for serious deal hunters',
    monthlyPrice: 4.99,
    yearlyPrice: 47.88,
    popular: true,
    features: [
      'Track up to 50 products',
      'Instant notifications (6hr vs 24hr)',
      '30-day price history',
      'Priority alerts',
      'Multiple store tracking',
      'Export data',
      'Push notifications',
      'Full As-Is scanning',
    ],
  },
  {
    id: 'pro_plus',
    name: 'Pro+',
    description: 'For power users and professionals',
    monthlyPrice: 9.99,
    yearlyPrice: 95.88,
    features: [
      'Unlimited products',
      'All Pro features',
      'API access',
      'Cross-country tracking',
      'Priority support',
      'Early feature access',
      'Real-time sale alerts',
      '365-day price history',
    ],
  },
];

// ============================================================================
// Hero Stats (Social Proof)
// ============================================================================

export const HERO_STATS: HeroStat[] = [
  {
    value: '10K+',
    label: 'Active Users',
    icon: 'users',
    iconBg: 'bg-blue-500/30',
  },
  {
    value: '500K+',
    label: 'Products Tracked',
    icon: 'package',
    iconBg: 'bg-purple-500/30',
  },
  {
    value: '1M+',
    label: 'Alerts Sent',
    icon: 'bell',
    iconBg: 'bg-amber-500/30',
  },
  {
    value: '$2M+',
    label: 'Savings Found',
    icon: 'trending-down',
    iconBg: 'bg-green-500/30',
  },
];

// ============================================================================
// FAQ Items
// ============================================================================

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes, you can cancel your subscription at any time. Your features will remain active until the end of your billing period.',
  },
  {
    question: 'Will I lose my data if I downgrade?',
    answer:
      "No, your data is safe. If you exceed plan limits after downgrading, you'll be asked to remove items before adding new ones.",
  },
  {
    question: 'Can I switch between plans?',
    answer:
      'Yes, you can upgrade or downgrade at any time. Changes take effect immediately and billing is prorated.',
  },
];

// ============================================================================
// Pricing Helpers
// ============================================================================

export const YEARLY_DISCOUNT_PERCENT = 20;

export function formatPrice(price: number): string {
  return price === 0 ? '$0' : `$${price.toFixed(2)}`;
}

export function getYearlySavings(monthlyPrice: number, yearlyPrice: number): number {
  const monthlyTotal = monthlyPrice * 12;
  return monthlyTotal - yearlyPrice;
}
